import { FC, useEffect, useMemo, useState } from "react";
import { FieldValues, useFormContext, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  AccordionHeader,
  AccordionHeaderText,
  AutoCompleteOption,
  FormAutoCompleteController,
  FormController,
  FormControllerCheckbox,
  MuiAutoComplete,
  MuiBox,
  MuiCheckbox,
  MuiGrid,
  MuiLink,
  MuiSelect,
  MuiTextField,
} from "../../../../components";

import { useLocation } from "react-router-dom";
import {
  useGetClientSalesOfficesList,
  useGetCountryListForDropdowns,
  useValidateOpportunityIdMutation,
} from "../../../../api/services/client/clientApis";
import { ValidateOpportunityPayload } from "../../../../api/services/client/models";
import { useGetCountryListByPage } from "../../../../api/services/common/commonApis";
import { NISConvertToProgramModel } from "../../../../api/services/nisParticipants/models";
import { useGetClientBillingDetailsByClientId } from "../../../../api/services/program/programApis";
import { ProgramTemplateListParams } from "../../../../api/services/programTemplate/models";
import {
  useGetAlternateDisplayName,
  useGetGroupedLineOfBusiness,
  useGetGroupedProgramTemplateList,
} from "../../../../api/services/programTemplate/programTemplateApis";
import { GetProjectListResponse } from "../../../../api/services/project/model";
import { useGetProjectList } from "../../../../api/services/project/projectApis";
import { ClientAutoComplete } from "../../../../components/clientAutoComplete/ClientAutoComplete";
import LabelWithInfoIcon from "../../../../components/labelWithInfoIcon/LabelWithInfoIcon";
import { MuiGroupedSelect } from "../../../../components/muiGroupedSelect/MuiGroupedSelect";
import { STATUS_BADGE_TYPE } from "../../../../components/statusBadge/StatusBadge";
import {
  BE_ERROR_CODES,
  DEBOUNCE_THRESHOLD,
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  PAGE_NAME_FOR_COUNTRY_DROPDOWN,
  PAGE_OPERATION_TYPE,
  TIMEOUT_300,
} from "../../../../helpers/constants";
import {
  getParsedBEErrors,
  getValueOrBlank,
  getValueOrDash,
  isEmpty,
  isErrorCode,
  isNotEmpty,
  setApiDataToDropDownOption,
  setGroupedApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { programStatusSelector } from "../../../../redux/slices/program/programSelector";
import {
  CreateProgramFormKey,
  OUTPLACEMENT,
  ProgramDetail,
} from "../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateProgramKeyInputWrapper } from "./CreateProgramKeyInput.style";

interface CreateProgramKeyInputProps extends FieldValues {
  pageId: string;
  isUpdateDetail?: boolean;
  programDetail?: ProgramDetail;
  apiErrorData: Error | null;
  programId?: string;
}

export const CreateProgramKeyInput: FC<CreateProgramKeyInputProps> = ({
  pageId,
  isUpdateDetail = false,
  programDetail,
  apiErrorData,
  programId,
}) => {
  const {
    control,
    getValues,
    setValue,
    clearErrors,
    setError,
    formState: { isDirty, dirtyFields, touchedFields },
  } = useFormContext();
  const location = useLocation();
  const programStatus = useSelector(programStatusSelector);
  const { commonLabels, fetchLabel, fetchComponentTitle } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const dispatch = useDispatch();
  const [templateList, setTemplateList] = useState([]);
  const [lineOfBusinessList, setLineOfBusinessList] = useState([]);
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(isUpdateDetail);
  const [isProgramTemplateEmpty, setIsProgramTemplateEmpty] = useState(false);

  const [inputValue, setInputValue] = useState("");
  const debouncedValue = useDebounce(inputValue, TIMEOUT_300);
  const [projectListOption, setProjectListOption] = useState<
    AutoCompleteOption[]
  >([]);

  const [isBillingHasOpportunityId, setIsBillingHasOpportunityId] =
    useState<boolean>(
      programDetail?.isOpportunityIdFromBillingConfiguration || false,
    );
  const [tooltipMessage, setTooltipMessage] = useState<string>("");
  const [showTooltipErrorIcon, setShowTooltipErrorIcon] =
    useState<boolean>(false);
  const [isOpportunityParentKeyChanged, setIsOpportunityParentKeyChanged] =
    useState<boolean>(false);

  const isFromNIS = location.state?.isFromNISParticipant ?? false;

  useEffect(() => {
    if (isFromNIS && location.state?.nisParticipantData) {
      const nisParticipantData: NISConvertToProgramModel =
        location.state.nisParticipantData;
      setValue(CreateProgramFormKey.client, nisParticipantData?.client?.id);
      setValue(
        CreateProgramFormKey.deliveryCountry,
        nisParticipantData?.country?.countryId,
      );
      callSalesOfficeApi(nisParticipantData?.client?.id);
    }
  }, [isFromNIS, location.state]);

  const {
    data: countryListData,
    isFetching: countryPending,
    isError: countryError,
  } = useGetCountryListForDropdowns();

  const {
    data: lineOfBusinessResponse,
    isFetching: businessPending,
    isError: lobError,
    refetchWithParams: refetchGroupedLineOfBusiness,
  } = useGetGroupedLineOfBusiness();

  const {
    data: programTemplates,
    isFetching: templatePending,
    isError: templateError,
    isSuccess: isProgramTemplateSuccess,
    refetchWithParams: getProgramTemplateList,
  } = useGetGroupedProgramTemplateList();

  const {
    data: alternateDisplayNameResponse,
    isFetching: alternateDisplayNamePending,
    isError: alternateDisplayNameError,
    refetchWithParams: refetchAlternateDisplayName,
  } = useGetAlternateDisplayName();

  const {
    data: projectList,
    isFetching: isProjectListLoading,
    refetchWithParams: refetchProjectList,
  } = useGetProjectList();

  const {
    data: validateOpportunityIdData,
    mutateAsync: validateOpportunityId,
    isError: isValidateOpportunityIdError,
    error: validateOpportunityIdError,
    isSuccess: isValidateOpportunityIdSuccess,
    isPending: isValidateOpportunityIdPending,
  } = useValidateOpportunityIdMutation();

  const {
    data: clientBillingDetails,
    isFetching: isClientBillingDetailsPending,
    isSuccess: isClientBillingDetailsGetApiSuccess,
    refetchWithParams: getClientBillingDetails,
  } = useGetClientBillingDetailsByClientId();

  const {
    data: clientSalesOfficeData,
    refetchWithParams: refetchClientSalesOfficeData,
    isFetching: isClientSalesOfficeFetching,
    isSuccess: isClientSalesOfficeSuccess,
  } = useGetClientSalesOfficesList();

  const clientId = useWatch({
    name: CreateProgramFormKey.client,
    control,
  });

  const lineOfBusinessId = useWatch({
    name: CreateProgramFormKey.lineOfBusiness,
    control,
  });

  const deliveryCountryId = useWatch({
    name: CreateProgramFormKey.deliveryCountry,
    control,
  });

  const programTemplateId = useWatch({
    name: CreateProgramFormKey.programTemplate,
    control,
  });

  const handleOpportunityErrors = (error: Error) => {
    if (
      isErrorCode(error, BE_ERROR_CODES.OPPORTUNITY_ID_INVALID) ||
      isErrorCode(error, BE_ERROR_CODES.OPPORTUNITY_ID_INVALID_VALIDATE_API)
    ) {
      setTooltipMessage("");
      setError(CreateProgramFormKey.opportunityId, {
        type: "custom",
        message: fetchLabel(
          programSiteCoreKeys.keyProgramDetails,
          programSiteCoreKeys.opportunityIDInvalidMessage,
        ),
      });
      setShowTooltipErrorIcon(false);
      return true;
    } else if (
      isErrorCode(
        error,
        BE_ERROR_CODES.VALIDATE_API_OPPORTUNITY_ID_ALREADY_MAPPED_TO_CLIENT,
      ) ||
      isErrorCode(
        error,
        BE_ERROR_CODES.OPPORTUNITY_ID_ALREADY_MAPPED_TO_CLIENT_PROGRAM_MODULE,
      )
    ) {
      const parsedError = getParsedBEErrors(error);
      const clientName = parsedError?.[0].message?.split("|")[1]?.trim() || "";
      setTooltipMessage(clientName);
      setError(CreateProgramFormKey.opportunityId, {
        type: "custom",
        message: fetchLabel(
          programSiteCoreKeys.keyProgramDetails,
          programSiteCoreKeys.opportunityIDAssociatedWithAnotherClient,
        ),
      });
      setShowTooltipErrorIcon(true);
      return true;
    } else if (
      isErrorCode(
        error,
        BE_ERROR_CODES.VALIDATE_API_OPPORTUNITY_ID_ALREADY_MAPPED_TO_PROGRAM,
      )
    ) {
      const parsedError = getParsedBEErrors(error);
      const programName = parsedError?.[0].message?.split("|")[0]?.trim() || "";
      setTooltipMessage(programName);
      setError(CreateProgramFormKey.opportunityId, {
        type: "custom",
        message: fetchLabel(
          programSiteCoreKeys.keyProgramDetails,
          programSiteCoreKeys.opportunityIDAssociatedWithProgramMessage,
        ),
      });
      setShowTooltipErrorIcon(true);
      return true;
    }
    return false;
  };

  // Handle Update/Save program errors
  useEffect(() => {
    if (apiErrorData) {
      handleOpportunityErrors(apiErrorData);
    }
  }, [apiErrorData]);

  useEffect(() => {
    if (isValidateOpportunityIdError && validateOpportunityIdError) {
      handleOpportunityErrors(validateOpportunityIdError);
    }
  }, [isValidateOpportunityIdError, validateOpportunityIdError]);

  useEffect(() => {
    if (isNotEmpty(clientId) && dirtyFields?.client) {
      setValue(CreateProgramFormKey.salesOfficeId, "");
      callSalesOfficeApi(clientId);
    }
    if (touchedFields?.client) resetProjectDetails(true);
  }, [clientId, dirtyFields?.client]);

  useEffect(() => {
    if (programDetail?.clientId && isUpdateDetail) {
      callSalesOfficeApi(programDetail?.clientId);
    }
  }, [isUpdateDetail, programDetail?.clientId]);

  const callSalesOfficeApi = (clientId: string) => {
    refetchClientSalesOfficeData(clientId);
  };

  useEffect(() => {
    const { projectKey } = getValues();
    if (isUpdateDetail && isNotEmpty(projectKey)) {
      setInputValue(projectKey);
    }
  }, [isUpdateDetail]);

  const resetProjectDetails = (resetCheckBox: boolean) => {
    if (resetCheckBox) {
      setValue(CreateProgramFormKey.isProjectTagged, false);
    }
    setValue(CreateProgramFormKey.projectKey, "");
    setValue(CreateProgramFormKey.projectName, "");
    clearErrors(CreateProgramFormKey.projectKey);
    clearErrors(CreateProgramFormKey.projectName);
    setProjectListOption([]);
    setInputValue("");
  };

  useEffect(() => {
    if (
      isNotEmpty(clientId) &&
      isNotEmpty(programTemplateId) &&
      (dirtyFields?.programTemplate || isDirty)
    ) {
      refetchAlternateDisplayName({ clientId, programTemplateId });
    }
  }, [clientId, programTemplateId]);

  useEffect(() => {
    if (!alternateDisplayNameResponse) return;

    const { name } = alternateDisplayNameResponse;

    setValue(
      CreateProgramFormKey.alternateName,
      isNotEmpty(name) ? name : EMPTY_VALUE_DASH,
    );
  }, [alternateDisplayNameResponse]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          businessPending ||
          templatePending ||
          countryPending ||
          alternateDisplayNamePending ||
          isProjectListLoading ||
          isClientBillingDetailsPending ||
          isValidateOpportunityIdPending ||
          isClientSalesOfficeFetching,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    businessPending,
    templatePending,
    countryPending,
    alternateDisplayNamePending,
    isProjectListLoading,
    isClientBillingDetailsPending,
    isValidateOpportunityIdPending,
    isClientSalesOfficeFetching,
  ]);

  useEffect(() => {
    if (
      countryError ||
      lobError ||
      templateError ||
      alternateDisplayNameError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [countryError, lobError, templateError, alternateDisplayNameError]);

  useEffect(() => {
    const { client, deliveryCountry, lineOfBusiness } = getValues();
    if (
      isNotEmpty(client) &&
      isNotEmpty(deliveryCountry) &&
      isNotEmpty(lineOfBusiness)
    ) {
      if (
        isDirty ||
        dirtyFields?.lineOfBusiness ||
        dirtyFields?.deliveryCountry
      ) {
        resetLineOfBusinessAndTemplate(false);
      }
      let params: ProgramTemplateListParams = {
        clientId: client,
        countryId: deliveryCountry,
        lobId: lineOfBusiness,
      };
      if (isFromNIS) {
        params = {
          ...params,
          solutionOfferingsName: OUTPLACEMENT,
        };
      }
      getProgramTemplateList(params);
    } else if (isDirty) {
      resetLineOfBusinessAndTemplate(false);
    }
  }, [lineOfBusinessId, deliveryCountryId, isFromNIS]);

  useEffect(() => {
    const isAllEmpty =
      programTemplates &&
      typeof programTemplates === "object" &&
      Object.values(programTemplates).every(
        (value) => Array.isArray(value) && value.length === 0,
      );

    if (isAllEmpty) {
      setTemplateList([]);
      setIsProgramTemplateEmpty(true);
    } else {
      setTemplateList(programTemplates);
      setIsProgramTemplateEmpty(false);
      clearErrors(CreateProgramFormKey.programTemplate);
    }
  }, [programTemplates]);

  const resetLineOfBusinessAndTemplate = (resetLOB: boolean) => {
    if (resetLOB) {
      setValue(CreateProgramFormKey.lineOfBusiness, "");
    }
    setValue(CreateProgramFormKey.programTemplate, "");
    setTemplateList([]);
  };

  useEffect(() => {
    if (touchedFields?.client) {
      resetLineOfBusinessAndTemplate(true);
    }

    const { client } = getValues();

    if (isNotEmpty(client)) {
      refetchGroupedLineOfBusiness({
        clientId: client,
      });
    } else {
      setLineOfBusinessList([]);
    }
  }, [clientId]);

  useEffect(() => {
    if (lineOfBusinessResponse) {
      setLineOfBusinessList(lineOfBusinessResponse);
    }
  }, [lineOfBusinessResponse]);

  useEffect(() => {
    if (debouncedValue.length >= DEBOUNCE_THRESHOLD && isNotEmpty(clientId)) {
      refetchProjectList({ keyword: debouncedValue, clientId: clientId });
    }
    setProjectListOption([]);
  }, [debouncedValue]);

  useEffect(() => {
    if (projectList && isNotEmpty(projectList)) {
      const options: AutoCompleteOption[] = projectList.map(
        (item: GetProjectListResponse) => ({
          id: item.id,
          label: item.projectKey,
          labelTwo: item.name,
        }),
      );
      setProjectListOption(options);
    } else {
      setProjectListOption([]);
    }
  }, [projectList]);

  const isProjectTagged = useWatch({
    name: CreateProgramFormKey.isProjectTagged,
    control,
  });

  const projectKeyWatch = useWatch({
    name: CreateProgramFormKey.projectKey,
    control,
  });

  useEffect(() => {
    const { projectKey } = getValues();

    const projectName = projectList?.find(
      (item: GetProjectListResponse) => item.id === projectKey,
    )?.name;

    isDirty && setValue(CreateProgramFormKey.projectName, projectName || "");
  }, [projectKeyWatch]);

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.keyProgramDetails, key);
  };

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const closedStatuses: string[] = [
    STATUS_BADGE_TYPE.closed,
    STATUS_BADGE_TYPE.conCon,
  ];
  const openStatuses: string[] = [
    STATUS_BADGE_TYPE.active,
    STATUS_BADGE_TYPE.open,
    STATUS_BADGE_TYPE.inActive,
  ];

  const shouldCheckBoxVisible: boolean = useMemo(() => {
    const { projectKey, isProjectTagged } = getValues();

    if (isUpdateDetail && isNotEmpty(projectKey) && isProjectTagged) {
      // hidden when update and already project is tagged case 1
      return false;
    }

    return true;
  }, [isUpdateDetail, programDetail]);

  const shouldCheckboxDisabled: boolean = useMemo(() => {
    if (isUpdateDetail) {
      if (closedStatuses.includes(programStatus)) return true;
      // case 4 Status of Program is NOT OPEN [CLOSED or CON-CON]

      if (
        openStatuses.includes(programStatus) &&
        programDetail?.isInvoiced === true
      )
        return true;
      // case 5 Status of Program is Open, Active, Inactive but Invoiced

      if (
        programStatus === STATUS_BADGE_TYPE.open &&
        programDetail?.isInvoiced === false
      )
        return false;
      //case 2 Status of Program is OPEN and not Invoiced

      if (
        (programStatus === STATUS_BADGE_TYPE.active ||
          programStatus === STATUS_BADGE_TYPE.inActive) &&
        programDetail?.isInvoiced === false
      )
        return false;
      // case 3 Status of Program is Active, Inactive but not Invoiced

      return false;
    }

    return isEmpty(clientId);
    //default when no client selected in add mode
  }, [isUpdateDetail, programStatus, programDetail, clientId]);

  const shouldProgramKeyDisabled: boolean = useMemo(() => {
    const { projectKey, isProjectTagged } = getValues();

    if (isUpdateDetail && isNotEmpty(projectKey) && isProjectTagged) {
      // case 1 Program, already mapped to a Project
      return true;
    }
    return false;
  }, [programStatus, isUpdateDetail]);

  const lineOfBusinessKeyToGroupMap = {
    lobWithBillingConfiguration: getLabel(
      programSiteCoreKeys.billingConfiguration,
    ),
    lobWithOutBillingConfiguration: getLabel(programSiteCoreKeys.otherLabel),
  };

  const programTemplateKeyToGroupMap = {
    programTemplateWithBillingConfig: getLabel(
      programSiteCoreKeys.billingConfiguration,
    ),
    programTemplateWithOutBillingConfig: getLabel(
      programSiteCoreKeys.otherLabel,
    ),
  };

  useEffect(() => {
    if (
      isOpportunityParentKeyChanged &&
      isNotEmpty(clientId) &&
      isNotEmpty(lineOfBusinessId) &&
      isNotEmpty(programTemplateId)
    ) {
      getClientBillingDetails(clientId, programTemplateId, lineOfBusinessId);
    }
  }, [clientId, lineOfBusinessId, programTemplateId]);

  useEffect(() => {
    if (
      isNotEmpty(clientBillingDetails) &&
      isClientBillingDetailsGetApiSuccess
    ) {
      const { opportunityId, opportunityName } = clientBillingDetails;

      setValue(
        CreateProgramFormKey.opportunityId,
        getValueOrBlank(opportunityId),
      );
      setValue(
        CreateProgramFormKey.opportunityName,
        getValueOrBlank(opportunityName),
      );
      setValue(CreateProgramFormKey.isOpportunityIdValidated, !!opportunityId);
      setIsBillingHasOpportunityId(isNotEmpty(opportunityId));
    }
  }, [clientBillingDetails, isClientBillingDetailsGetApiSuccess]);

  useEffect(() => {
    if (isClientSalesOfficeSuccess && clientSalesOfficeData) {
      if (!isUpdateDetail) {
        // don't set sales office in Update to avoid overwriting from Client again
        const selectedSalesOfficeId = clientSalesOfficeData.find(
          (office) => office.isSelected,
        )?.id;
        setValue(CreateProgramFormKey.salesOfficeId, selectedSalesOfficeId);
      }
    }
  }, [isClientSalesOfficeSuccess, clientSalesOfficeData, isUpdateDetail]);

  useEffect(() => {
    const opportunityId = getValueOrBlank(programDetail?.opportunityId);
    setValue(CreateProgramFormKey.opportunityId, opportunityId);
    setValue(
      CreateProgramFormKey.opportunityName,
      getValueOrDash(programDetail?.opportunityName),
    );
    setValue(CreateProgramFormKey.isOpportunityIdValidated, !!opportunityId);
  }, []);

  const resetOpportunityIdValidation = () => {
    setIsBillingHasOpportunityId(false);
    setTooltipMessage("");
    setShowTooltipErrorIcon(false);
    clearErrors(CreateProgramFormKey.opportunityId);
    setValue(CreateProgramFormKey.isOpportunityIdValidated, false);
    setValue(CreateProgramFormKey.opportunityName, EMPTY_VALUE_DASH);
  };

  useEffect(() => {
    if (isValidateOpportunityIdSuccess && validateOpportunityIdData) {
      setValue(CreateProgramFormKey.isOpportunityIdValidated, true);
      clearErrors(CreateProgramFormKey.opportunityId);
      setValue(
        CreateProgramFormKey.opportunityName,
        getValueOrBlank(validateOpportunityIdData?.name),
      );

      setShowTooltipErrorIcon(false);
    }
  }, [isValidateOpportunityIdSuccess, validateOpportunityIdData]);

  const handleOpportunityIdValidation = () => {
    const { opportunityId } = getValues();
    if (isNotEmpty(opportunityId)) {
      const validateOpportunityPayload: ValidateOpportunityPayload = {
        clientId: getValueOrBlank(clientId || programDetail?.clientId),
        opportunityId: getValueOrBlank(opportunityId),
        contractDocumentId: "",
        programId: getValueOrBlank(programId),
        isForDocument: false,
        isForProgram: true,
      };
      validateOpportunityId(validateOpportunityPayload);
    }
  };

  const onProductLineChange = () => {
    setIsOpportunityParentKeyChanged(true);
    setValue(CreateProgramFormKey.programTemplate, "");
    setTemplateList([]);
    if (isBillingHasOpportunityId) {
      setValue(CreateProgramFormKey.opportunityId, "");
      resetOpportunityIdValidation();
    }
  };

  const onProgramTemplateChange = () => {
    setIsOpportunityParentKeyChanged(true);
    if (isBillingHasOpportunityId) {
      setValue(CreateProgramFormKey.opportunityId, "");
      resetOpportunityIdValidation();
    }
  };

  const getSelectedClientAutoCompleteValue = () => {
    if (isUpdateDetail) {
      return {
        selectedValue: {
          id: getValueOrBlank(programDetail?.clientId),
          label: getValueOrBlank(programDetail?.clientName),
          labelTwo: getValueOrBlank(programDetail?.clientCode),
        },
      };
    }

    if (isFromNIS) {
      return {
        selectedValue: {
          id: getValueOrBlank(location.state?.nisParticipantData?.client?.id),
          label: getValueOrBlank(
            location.state?.nisParticipantData?.client?.name,
          ),
        },
      };
    }
  };

  return (
    <CreateProgramKeyInputWrapper>
      <AccordionHeader
        title={renderHeader(
          fetchComponentTitle(programSiteCoreKeys.keyProgramDetails),
        )}
        isExpanded={true}
        className={`key-input-accordion ${isUpdateDetail && "update-input-general"} `}
        showExpandCollapseIcon={false}
      >
        <form data-testid="create-program-key-input">
          <MuiGrid container className="form-container">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <ClientAutoComplete
                isMandatory
                label={getLabel(programSiteCoreKeys.client)}
                formId={CreateProgramFormKey.client}
                control={control}
                disabled={isUpdateDetail || isFromNIS}
                isActiveClientOnly
                {...getSelectedClientAutoCompleteValue()}
              />
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProgramFormKey.deliveryCountry}
              >
                <MuiSelect
                  label={getLabel(programSiteCoreKeys.deliveryCountry)}
                  options={setApiDataToDropDownOption(countryListData, true)}
                  visibleDefaultSelectOption
                  value=""
                  id={`${pageId}-${CreateProgramFormKey.deliveryCountry}`}
                  isMandatory
                  placeholder={commonLabels.selectPlaceHolder}
                  showSearch
                  searchPlaceholder={commonLabels.search}
                  disabled={
                    (isUpdateDetail &&
                      programStatus === STATUS_BADGE_TYPE.active) ||
                    isFromNIS
                  }
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProgramFormKey.lineOfBusiness}
              >
                <MuiGroupedSelect
                  label={getLabel(programSiteCoreKeys.productLine)}
                  options={setGroupedApiDataToDropDownOption(
                    lineOfBusinessList,
                    lineOfBusinessKeyToGroupMap,
                  )}
                  id={`${pageId}-${CreateProgramFormKey.lineOfBusiness}`}
                  showSearch
                  isMandatory
                  defaultPlaceholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  onChange={onProductLineChange}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProgramFormKey.programTemplate}
              >
                <MuiGroupedSelect
                  label={getLabel(programSiteCoreKeys.programTemplate)}
                  options={setGroupedApiDataToDropDownOption(
                    templateList,
                    programTemplateKeyToGroupMap,
                  )}
                  id={`${pageId}-${CreateProgramFormKey.programTemplate}`}
                  showSearch
                  isMandatory
                  defaultPlaceholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  onChange={onProgramTemplateChange}
                />
              </FormController>

              {isProgramTemplateEmpty && isProgramTemplateSuccess && (
                <LabelWithInfoIcon
                  label={getLabel(
                    programSiteCoreKeys.noProgramTemplateAvailable,
                  )}
                  tooltipMessage={getLabel(
                    programSiteCoreKeys.noProgramTemplateFoundMessage,
                  )}
                />
              )}
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiBox className="opportunity-id-container">
                <FormController
                  control={control}
                  name={CreateProgramFormKey.opportunityId}
                >
                  <MuiTextField
                    id={`${pageId}-${CreateProgramFormKey.opportunityId}`}
                    name={CreateProgramFormKey.opportunityId}
                    label={getLabel(programSiteCoreKeys.opportunityID)}
                    placeholder={commonLabels.inputPlaceHolder}
                    isErrorInfoIconEnabled={showTooltipErrorIcon}
                    tooltipMessage={tooltipMessage}
                    tooltipHeaderTitle={commonLabels.details}
                    onInput={resetOpportunityIdValidation}
                    requiredARIALabel={commonARIALabel.required}
                    maxLength={MAX_LENGTH_50}
                    disabled={isBillingHasOpportunityId}
                  />
                </FormController>
                {!isBillingHasOpportunityId && (
                  <MuiLink
                    className="validate-link"
                    onClick={handleOpportunityIdValidation}
                  >
                    {commonLabels.validate}
                  </MuiLink>
                )}
              </MuiBox>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProgramFormKey.opportunityName}
              >
                <MuiTextField
                  isMandatory
                  disabled
                  label={getLabel(programSiteCoreKeys.opportunityName)}
                  placeholder={EMPTY_VALUE_DASH}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProgramFormKey.alternateName}
              >
                <MuiTextField
                  label={getLabel(programSiteCoreKeys.alternateDisplayName)}
                  placeholder={commonLabels.inputPlaceHolder}
                  id={`${pageId}-${CreateProgramFormKey.alternateName}`}
                  disabled
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProgramFormKey.salesOfficeId}
              >
                <MuiSelect
                  isMandatory
                  id={`${pageId}-${CreateProgramFormKey.salesOfficeId}`}
                  label={getLabel(programSiteCoreKeys.salesOffice)}
                  visibleDefaultSelectOption
                  showSearch
                  options={setApiDataToDropDownOption(clientSalesOfficeData)}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  disabled={isUpdateDetail}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProgramFormKey.nextPersona}
              >
                <MuiTextField
                  label={getLabel(programSiteCoreKeys.nextPersona)}
                  placeholder={commonLabels.inputPlaceHolder}
                  id={`${pageId}-${CreateProgramFormKey.nextPersona}`}
                  disabled
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>

          <MuiBox className="line-container" />

          {shouldCheckBoxVisible && (
            <MuiBox className="tag-project-container">
              <FormControllerCheckbox
                control={control}
                name={CreateProgramFormKey.isProjectTagged}
              >
                <MuiCheckbox
                  className="tag-project-checkbox"
                  checkboxLabel={getLabel(
                    programSiteCoreKeys.tagAProjectCheckBox,
                  )}
                  id={`${pageId}-${CreateProgramFormKey.isProjectTagged}`}
                  data-testid={`${pageId}-${CreateProgramFormKey.isProjectTagged}`}
                  disabled={shouldCheckboxDisabled}
                  onCheckedChange={(checked: boolean) => {
                    setValue(CreateProgramFormKey.isProjectTagged, checked);
                    if (!checked) {
                      resetProjectDetails(false);
                    }
                  }}
                />
              </FormControllerCheckbox>
            </MuiBox>
          )}

          {isProjectTagged && (
            <MuiGrid container className="project-details-container">
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <FormAutoCompleteController
                  control={control}
                  name={CreateProgramFormKey.projectKey}
                >
                  <MuiAutoComplete
                    label={getLabel(programSiteCoreKeys.projectKey)}
                    placeholder={commonLabels.typeToSearchPlaceholder3Chars}
                    requiredARIALabel={commonARIALabel.required}
                    options={projectListOption}
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    isInputValueControlled
                    id={`${pageId}-${CreateProgramFormKey.projectKey}`}
                    noOptionsText={commonLabels.noResult}
                    disabled={shouldProgramKeyDisabled}
                    isMandatory={isProjectTagged}
                  />
                </FormAutoCompleteController>
              </MuiGrid>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <FormController
                  control={control}
                  name={CreateProgramFormKey.projectName}
                >
                  <MuiTextField
                    label={getLabel(programSiteCoreKeys.projectName)}
                    id={`${pageId}-${CreateProgramFormKey.projectName}`}
                    disabled
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
            </MuiGrid>
          )}
        </form>
      </AccordionHeader>
    </CreateProgramKeyInputWrapper>
  );
};
